import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './GloathPage.css';

const GloathPage = () => {
  const navigate = useNavigate();
  const [navScrolled, setNavScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [mechSlide, setMechSlide] = useState(0);
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('gp-visible');
            observer.unobserve(entry.target);
          }
        }),
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );
    document.querySelectorAll('.gp-reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Scroll-spy: highlight active nav link based on which section is in view
  useEffect(() => {
    const sectionIds = [
      'gp-problem', 'gp-loop', 'gp-mechanics', 'gp-level-design',
      'gp-character', 'gp-kpis', 'gp-rice', 'gp-monetization', 'gp-roadmap',
    ];
    const spy = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { threshold: 0, rootMargin: '-15% 0px -75% 0px' }
    );
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) spy.observe(el);
    });
    return () => spy.disconnect();
  }, []);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const generatePDF = async () => {
    setIsPdfLoading(true);
    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas'),
      ]);

      // 1. Scroll to top
      const prevScroll = window.scrollY;
      window.scrollTo(0, 0);

      // 2. Force EVERY reveal element visible so opacity:0 sections don't render black
      document.querySelectorAll('.gp-reveal').forEach((el) => {
        el.classList.add('gp-visible');
      });

      // 3. Wait for all CSS transitions to finish (longest is 0.55s + stagger)
      await new Promise((r) => setTimeout(r, 900));

      // 4. Capture full body
      const canvas = await html2canvas(document.body, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#F7F8FA',
        logging: false,
        ignoreElements: (el) =>
          el.classList && (
            el.classList.contains('gp-nav') ||
            el.classList.contains('gp-hero-btns') ||
            el.classList.contains('gp-cta-btns')
          ),
        onclone: (_doc, body) => {
          body.style.paddingTop = '0';

          // Inject PDF-only header (not visible on the live page)
          const hdr = _doc.createElement('div');
          hdr.style.cssText = [
            'background:#FFFFFF',
            'padding:28px 56px',
            'display:flex',
            'justify-content:space-between',
            'align-items:center',
            'border-bottom:2px solid #7C5CFF',
            "font-family:'DM Sans',system-ui,sans-serif",
            'box-sizing:border-box',
            'width:100%',
          ].join(';');
          hdr.innerHTML = `
            <div style="display:flex;flex-direction:column;gap:4px;">
              <span style="color:#5B3DE0;font-size:13px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;">A PM Case Study</span>
              <span style="color:#0F1626;font-size:22px;font-weight:700;letter-spacing:0.04em;">NEVA ERDOĞAN</span>
            </div>
            <div style="display:flex;flex-direction:column;align-items:flex-end;gap:6px;">
              <span style="color:#5B3DE0;font-size:12px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;">Connect</span>
              <span style="color:#4B5468;font-size:14px;font-weight:400;">linkedin.com/in/nevaerdogan</span>
            </div>
          `;
          body.insertBefore(hdr, body.firstChild);
        },
      });

      window.scrollTo(0, prevScroll);

      // 5. Build a SINGLE long-page PDF (no A4 slicing → no content splits)
      const imgData = canvas.toDataURL('image/jpeg', 0.9);
      // Convert canvas px → mm  (96 dpi: 1px = 0.2646mm, but we captured at scale 1.5)
      const MM_PER_PX = 0.2646;
      const pdfW = (canvas.width / 1.5) * MM_PER_PX;
      const pdfH = (canvas.height / 1.5) * MM_PER_PX;

      const pdf = new jsPDF({
        orientation: pdfH > pdfW ? 'portrait' : 'landscape',
        unit: 'mm',
        format: [pdfW, pdfH],
      });

      pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, pdfH);
      pdf.save('Gloath-Case-Study.pdf');
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('PDF oluşturulamadı. Lütfen tekrar deneyin.');
    } finally {
      setIsPdfLoading(false);
    }
  };

  const navLinks = [
    { label: 'Problem', id: 'gp-problem' },
    { label: 'Loop', id: 'gp-loop' },
    { label: 'Mechanics', id: 'gp-mechanics' },
    { label: 'Level Design', id: 'gp-level-design' },
    { label: 'Character', id: 'gp-character' },
    { label: 'KPIs', id: 'gp-kpis' },
    { label: 'RICE', id: 'gp-rice' },
    { label: 'Monetization', id: 'gp-monetization' },
    { label: 'Roadmap', id: 'gp-roadmap' },
  ];

  const stats = [
    { value: '40%+', label: 'D1 Retention Target', note: 'vs 25–30% industry avg', color: '#EA580C' },
    { value: '15–20%', label: 'D7 Retention Target', note: 'Royal Match benchmark: ~20%', color: '#16A34A' },
    { value: '8–10min', label: 'Avg Session Length', note: 'Main loop + 1 side game', color: '#2563EB' },
    { value: '~60%', label: 'Level Fail Rate', note: 'Intentional — creates IAP window', color: '#DC2626' },
    { value: '2–5%', label: 'Conversion Rate', note: '3 entry points vs industry 1', color: '#D97706' },
  ];

  const problemCards = [
    {
      emoji: '📉',
      title: 'The Problem',
      text: 'The match-3 genre is experiencing measurable player fatigue. Candy Crush, Royal Match, and Toon Blast share near-identical core mechanics. D7 retention across the category has dropped from 22% in 2020 to 16% in 2024. UA creative performance is declining because ads are indistinguishable from each other.',
    },
    {
      emoji: '🎯',
      title: 'The Opportunity',
      text: 'Match-2 — tap any two adjacent same-color tiles, no dragging — is almost entirely unexplored. It reduces per-move cognitive load, enables faster sessions, and works perfectly one-handed. Tutorial completes 38% faster in playtests. The mechanic is different enough to anchor UA creatives.',
      featured: true,
    },
    {
      emoji: '🏦',
      title: 'Market Size',
      text: 'Global casual puzzle market: $8.2B (2024), projected $11.4B by 2027. Female players aged 25–44 represent 53% of casual players with highest LTV — $4.20 ARPU vs $2.80 for male same-age cohort. This demographic responds strongest to character-driven narratives.',
    },
    {
      emoji: '🥊',
      title: 'Competitive Benchmarks',
      text: 'Royal Match D1 ~45% | D7 ~20% · Toon Blast D1 ~38% | D7 ~18% · Candy Crush D1 ~35% | D7 ~15% · Gloath targets Royal Match tier by combining character-driven retention with mechanic differentiation none of the three currently offer.',
    },
  ];

  const loopTopNodes = [
    { emoji: '🎮', label: 'Play Level' },
    { emoji: '🧀', label: 'Earn Cheese' },
    { emoji: '🗺️', label: 'Progress Map' },
  ];
  const loopBottomNodes = [
    { emoji: '🏆', label: 'Harder Levels' },
    { emoji: '⚡', label: 'Gain Power' },
    { emoji: '🔓', label: 'Unlock Systems' },
  ];

  const mechanicCards = [
    {
      emoji: '🧀',
      title: 'Mozzarella Booster',
      subtitle: "Gloath's primary differentiator — no competitor has this",
      text: 'Triggered by a specific 3-match pattern. Player chooses a direction; the cheese stretches across tiles, chain-clearing in that path. The differentiator is agency — every other mobile booster is a passive explosion. Mozzarella requires a directional choice, making wins feel earned and shareable. The stretch animation is the UA creative hook.',
      tag: '⚡ Player agency + viral potential',
      featured: true,
    },
    {
      emoji: '🐱',
      title: 'Cat Obstacle',
      subtitle: 'Progressive boss — first appears level 8',
      text: 'Occupies 3×3 area. Multi-hit, scales by level. Visual states: sleeping → alert → escapes. Creates external blame attribution ("the cat blocked me") which preserves player self-esteem and increases retry rate. Most important monetization trigger in the game.',
      tag: 'Highest RICE score in backlog',
    },
    {
      emoji: '🪤',
      title: 'Trap',
      subtitle: 'Risk mechanic — first appears level 15',
      text: 'Locked tiles that spread restriction on incorrect adjacent matches. Slows deliberation, increases engagement depth per session. Prevents match-2 from feeling too easy at higher levels.',
      tag: 'Increases deliberation → session depth',
    },
    {
      emoji: '☣️',
      title: 'Sewer',
      subtitle: 'Spreading threat — first appears level 22',
      text: 'Spreads green slime turn-by-turn. Must be contained within move budget. Creates urgency that makes wins feel earned. Significantly increases willingness-to-pay for extra moves.',
      tag: 'Urgency → IAP conversion spike',
    },
  ];

  const levelInsights = [
    { emoji: '🎯', title: 'Fair Challenge', text: 'Every level has a clear solution. No randomness as the primary obstacle.' },
    { emoji: '🧠', title: 'Strategic Choices', text: 'Plan beats random. Players feel smart, not lucky.' },
    { emoji: '🔄', title: 'Retention', text: 'Curiosity and "one more try" feeling engineered into each level.' },
    { emoji: '💰', title: 'Monetization', text: 'Booster use moments are designed, not accidental.' },
  ];

  const skinCards = [
    {
      emoji: '🦸',
      title: 'Cheesy Hero',
      badge: 'EVENT UNLOCK — Hero Week only',
      text: 'Superhero costume with G-shield. Unlocked only during a limited-time live event — never returns to the store. Creates FOMO urgency that drives session frequency spikes. Event-only exclusivity is intentional: scarcity is the retention mechanic, not the skin itself.',
      accent: '#D97706',
    },
    {
      emoji: '👨‍🍳',
      title: 'Mozzarella Master',
      badge: 'COLLECTION UNLOCK — Complete Fromage Set',
      text: "Chef uniform referencing the game's signature booster. Unlock condition: complete 25 rare cards. Primary driver of D7–D30 retention because it creates a long-horizon visible goal. Players chasing this skin check in daily.",
      accent: '#D97706',
    },
    {
      emoji: '👑',
      title: 'Gold Delight',
      badge: 'PREMIUM IAP — $29.99',
      text: 'All-gold Gloath with crown. Targets whale segment (<1% of players, 40–60% of IAP revenue). Functions as price anchor — $4.99 starter packs feel like bargains by comparison. All-gold palette drives organic UA: players screenshot and share.',
      accent: '#D97706',
    },
  ];

  const kpiCards = [
    {
      value: '40%+',
      metric: 'D1 Retention',
      why: 'Match-2 reduces tutorial friction. Gloath character creates instant emotional hook in first 5 minutes. Lower cognitive barrier = higher first-session completion.',
      benchmark: 'Royal Match ~45% · Toon Blast ~38% · Industry avg ~27%',
      color: '#D97706',
    },
    {
      value: '15–20%',
      metric: 'D7 Retention',
      why: 'Collection system creates 5–7 day goals. Side games provide session continuity when main-loop lives are exhausted. Without these two systems, D7 drops significantly.',
      benchmark: 'Royal Match ~20% · Toon Blast ~18%',
      color: '#16A34A',
    },
    {
      value: '8–10 min',
      metric: 'Avg Session Length',
      why: 'Main loop (2–3 levels) = 5–6 min. One side game session = 3–4 min. This number directly justifies the side game investment — without them, sessions stay at 5–6 min.',
      benchmark: 'Toon Blast ~7–9 min · Target: +1 min via side games',
      color: '#EA580C',
    },
    {
      value: '~60%',
      metric: 'Level Fail Rate',
      why: 'Intentional design, not a bug. This is the window where "+5 moves" IAP offers convert at 8–12%. Too low = no monetization pressure. Too high (>75%) = frustration churn. 60% is the proven sweet spot.',
      benchmark: 'Royal Match levels 20–50: ~58% fail rate',
      color: '#DC2626',
    },
    {
      value: '2–5%',
      metric: 'Conversion Rate',
      why: '3 IAP entry points (level fail + side game timer + skin purchase) vs industry standard of 1. Upper range (5%) requires P1 skin + collection systems live.',
      benchmark: 'Industry avg ~2–3% · Target upper: 5% post-P1',
      color: '#0D9488',
    },
  ];

  const riceRows = [
    { emoji: '🐱', feature: 'Cat Obstacle', reach: '100%', impact: 3, conf: '90%', effort: '2 wks', score: 135, phase: 'P0 MVP', featured: true },
    { emoji: '🎮', feature: 'Match-2 Engine', reach: '100%', impact: 5, conf: '95%', effort: '8 wks', score: 119, phase: 'P0 MVP' },
    { emoji: '🧀', feature: 'Mozzarella Booster', reach: '80%', impact: 4, conf: '85%', effort: '3 wks', score: 91, phase: 'P0 MVP', featured: true },
    { emoji: '👕', feature: 'Character Skins', reach: '90%', impact: 3, conf: '85%', effort: '4 wks', score: 57, phase: 'P1 Post-Launch' },
    { emoji: '📚', feature: 'Collection System', reach: '70%', impact: 5, conf: '80%', effort: '6 wks', score: 47, phase: 'P1 Post-Launch' },
    { emoji: '🔀', feature: 'Merge Side Game', reach: '60%', impact: 4, conf: '75%', effort: '8 wks', score: 23, phase: 'P1 Post-Launch' },
    { emoji: '🎉', feature: 'Live Ops Events', reach: '80%', impact: 5, conf: '65%', effort: '12 wks', score: 22, phase: 'P2 Growth' },
    { emoji: '⛏', feature: 'Digging Mode', reach: '55%', impact: 3, conf: '70%', effort: '10 wks', score: 12, phase: 'P2 Growth' },
    { emoji: '🏰', feature: 'Guild System', reach: '40%', impact: 4, conf: '55%', effort: '16 wks', score: 6, phase: 'P3 Scale' },
  ];

  const phaseClass = (phase) => {
    if (phase === 'P0 MVP') return 'gp-phase-p0';
    if (phase === 'P1 Post-Launch') return 'gp-phase-p1';
    if (phase === 'P2 Growth') return 'gp-phase-p2';
    return 'gp-phase-p3';
  };

  const monetizationMoments = [
    {
      num: '①',
      title: 'Level Fail → "+5 Moves" ($0.99)',
      text: 'Appears immediately after fail animation. Loss aversion is active — the player just spent 2–3 minutes on that level. Price anchored low. Converts at 8–12% of fail events in benchmark data.',
    },
    {
      num: '②',
      title: '3rd Consecutive Fail → 50% Off Bundle',
      text: 'After 3 fails on the same level, discounted booster pack appears. Shifts player frame from "this game is unfair" to "I just need better tools." Reduces frustration churn while converting resistors.',
    },
    {
      num: '③',
      title: 'Pre-Level Booster Suggestion (AI-Triggered)',
      text: 'On levels where difficulty model predicts >75% fail probability, a booster kit is suggested before the level starts. Proactive monetization — before the fail, not after.',
    },
    {
      num: '④',
      title: 'Side Game Speed-Up (Impatience)',
      text: 'Merge evolution timers and Digging energy have real-time countdowns. Zero failure emotion required — pure impatience. Lowest-friction purchase in the game.',
    },
  ];

  const funnelSteps = [
    { label: 'Install', pct: '100%', width: '100%', color: '#2563EB' },
    { label: 'D1 Retained', pct: '40%+', width: '78%', color: '#16A34A' },
    { label: 'D7 Engaged', pct: '15–20%', width: '52%', color: '#D97706' },
    { label: 'First Payer', pct: '2–5%', width: '30%', color: '#DC2626' },
    { label: 'Whale / VIP', pct: '<1%', width: '12%', color: '#D97706' },
  ];

  const risks = [
    {
      title: 'Match-2 feels too simple → disengagement at level 20+',
      likelihood: 'Medium',
      likelihoodClass: 'gp-pill-medium',
      impact: 'High',
      impactClass: 'gp-pill-high',
      mitigation: 'Obstacle complexity ramps: Cat at L8, Trap at L15, Sewer at L22. Playtest gate: session length must not drop below 7 min at L25.',
    },
    {
      title: 'Mozzarella Booster too hard to discover organically',
      likelihood: 'High',
      likelihoodClass: 'gp-pill-high',
      impact: 'Medium',
      impactClass: 'gp-pill-medium',
      mitigation: 'Tutorial forces first use at L3. Achievement badge on first trigger. UA creatives center the stretch animation in the first 3 seconds.',
    },
    {
      title: 'Side games cannibalize main loop time',
      likelihood: 'Medium',
      likelihoodClass: 'gp-pill-medium',
      impact: 'Medium',
      impactClass: 'gp-pill-medium',
      mitigation: 'Energy gate: max 2 free sessions/day. Main loop lives refill on a separate 25-min timer — different resource pool entirely.',
    },
    {
      title: 'Cat obstacle perceived as unfair (3×3 too large)',
      likelihood: 'Low',
      likelihoodClass: 'gp-pill-low',
      impact: 'High',
      impactClass: 'gp-pill-high',
      mitigation: 'Playtest threshold: <15% "unfair" exit survey sentiment. Sleeping→alert animation provides advance warning before activation.',
    },
    {
      title: 'UA cost exceeds LTV → unsustainable growth',
      likelihood: 'Medium',
      likelihoodClass: 'gp-pill-medium',
      impact: 'High',
      impactClass: 'gp-pill-high',
      mitigation: 'Mozzarella stretch moment designed for organic share content. Character IP supports influencer program. K-factor target: >0.3.',
    },
  ];

  const roadmapPhases = [
    {
      phase: 'FOUNDATION',
      period: 'Month 1–4',
      dotColor: '#D97706',
      items: [
        'Match-2 engine (60 levels)',
        'Mozzarella booster',
        'Cat obstacle system',
        'Basic skin shop',
        'Soft launch: Turkey, Poland, Philippines',
      ],
      exit: 'D1 ≥38% · D7 ≥14%',
    },
    {
      phase: 'RETENTION',
      period: 'Month 5–7',
      dotColor: '#16A34A',
      items: [
        'Collection system',
        'Merge side game',
        'Full skin system (3 skins)',
        'IAP price ladder',
        '100 total levels',
      ],
      exit: 'D7 ≥17% · ARPDAU ≥$0.08',
    },
    {
      phase: 'GROWTH',
      period: 'Month 8–11',
      dotColor: '#7C3AED',
      items: [
        'Digging mode',
        'First live event (Hero Week)',
        'Leaderboard + friend system',
        'Guild beta',
        '200 total levels',
      ],
      exit: 'D30 ≥5% · K-factor ≥0.3',
    },
    {
      phase: 'SCALE',
      period: 'Month 12+',
      dotColor: '#0D9488',
      items: [
        '8-language localization',
        'App Store feature push',
        'Influencer UA program',
        'Seasonal event calendar',
      ],
      exit: 'Global top 50 casual chart · LTV ≥$3.20',
    },
  ];

  const tradeoffs = [
    {
      decision: 'Core mechanic',
      chose: 'Match-2 tap',
      rejected: 'Match-3 drag',
      reasoning: 'Differentiation in saturated market. Lower friction → higher D1. Newsworthy enough to anchor UA creatives.',
    },
    {
      decision: 'Ad strategy',
      chose: 'Rewarded only',
      rejected: 'Interstitial ads',
      reasoning: 'Session quality. Higher eCPM. Critical trust signal for core female demographic.',
    },
    {
      decision: 'Side game access',
      chose: 'Energy-gated',
      rejected: 'Unlimited play',
      reasoning: 'Creates IAP surface. Prevents side game replacing main loop. Scarcity makes each session feel valuable.',
    },
    {
      decision: 'Skin unlock',
      chose: 'Event + collect + buy',
      rejected: 'Purchase only',
      reasoning: 'All 3 player segments need aspiration. Non-payers drive social proof and organic UA.',
    },
    {
      decision: 'Cat obstacle size',
      chose: '3×3 large',
      rejected: '1×1 or 2×2',
      reasoning: 'Emotional impact requires a visible threat. Personality over pure game balance.',
    },
  ];

  return (
    <div className="gp-page">
      {/* ── Sticky Nav ── */}
      <nav className={`gp-nav${navScrolled ? ' gp-nav--scrolled' : ''}`}>
        <div className="gp-nav-inner">
          <button className="gp-back-btn" onClick={() => navigate('/')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
        </svg>
        Back
      </button>
          <span className="gp-nav-brand">Gloath</span>
          <div className="gp-nav-links">
            {navLinks.map((link) => (
              <button
                key={link.id}
                className={`gp-nav-link${activeSection === link.id ? ' gp-nav-link--active' : ''}`}
                onClick={() => scrollTo(link.id)}
              >
                {link.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* ── §1 Hero ── */}
      <section className="gp-section gp-hero">
        <div className="gp-container gp-hero-grid">
          <div className="gp-hero-left gp-reveal">
            <div className="gp-badges">
              {['Match-2 Puzzle', 'Self-Initiated', 'PM Case Study'].map((b) => (
                <span key={b} className="gp-pill">{b}</span>
              ))}
            </div>
            <h1 className="gp-hero-h1">
              <span className="gp-h1-main">Gloath</span>
              <span className="gp-h1-muted"> the mouse who</span>
              <span className="gp-h1-accent"> loves cheese.</span>
            </h1>
            <p className="gp-hero-desc">
              A self-initiated product case study exploring character-driven casual game design, layered
              progression systems, and F2P monetization architecture. Every decision is benchmarked and
              reasoned.
            </p>
            <div className="gp-hero-btns">
              <button
                className={`gp-btn gp-btn--primary${isPdfLoading ? ' gp-btn--loading' : ''}`}
                onClick={generatePDF}
                disabled={isPdfLoading}
              >
                {isPdfLoading ? '⏳ Generating PDF…' : '↓ Download Case Study PDF'}
              </button>
              <button className="gp-btn gp-btn--ghost" onClick={() => scrollTo('gp-problem')}>
                Read Case Study →
              </button>
            </div>
          </div>

          <div className="gp-hero-right gp-reveal" style={{ '--i': 1 }}>
            {/* iPhone mockup */}
            <div className="gp-iphone">
              <div className="gp-iphone-btn-side" />
              <div className="gp-iphone-btn-vol-1" />
              <div className="gp-iphone-btn-vol-2" />
              <div className="gp-iphone-btn-mute" />
              <div className="gp-iphone-frame">
                <div className="gp-iphone-island" />
                <div className="gp-iphone-screen">
                  <img src="/gloath/GLOATH.png" alt="Gloath the mouse character" className="gp-hero-img" />
                </div>
                <div className="gp-iphone-home-bar" />
              </div>
            </div>
            <div className="gp-meta-grid">
              <div className="gp-meta-item">
                <span className="gp-meta-val">Match-2</span>
                <span className="gp-meta-lbl">Core Mechanic</span>
              </div>
              <div className="gp-meta-item">
                <span className="gp-meta-val">F2P</span>
                <span className="gp-meta-lbl">Business Model</span>
              </div>
              <div className="gp-meta-item">
                <span className="gp-meta-val">18–45</span>
                <span className="gp-meta-lbl">Target Age</span>
              </div>
              <div className="gp-meta-item">
                <span className="gp-meta-val">3 Skins</span>
                <span className="gp-meta-lbl">Launch Cosmetics</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── §2 KPI Stats Bar ── */}
      <section className="gp-section gp-stats-section">
        <div className="gp-container">
          <div className="gp-stats-grid">
            {stats.map((s, i) => (
              <div
                key={i}
                className="gp-stat-card gp-reveal"
                style={{ '--i': i, '--accent': s.color }}
              >
                <div className="gp-stat-value" style={{ color: s.color }}>{s.value}</div>
                <div className="gp-stat-label">{s.label}</div>
                <div className="gp-stat-note">{s.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── §3 Problem & Opportunity ── */}
      <section className="gp-section" id="gp-problem">
        <div className="gp-container">
          <div className="gp-section-label gp-reveal">01 — Problem &amp; Opportunity</div>
          <h2 className="gp-section-h2 gp-reveal">
            The casual puzzle market is saturated. Match-2 is not.
          </h2>
          <p className="gp-section-intro gp-reveal">
            Match-3 has 20+ years of player education and 3 dominant titles. The format's average D7
            retention has declined 4pp since 2020. Players want the progression loop — not the same drag
            mechanic.
          </p>
          <div className="gp-2x2-grid">
            {problemCards.map((c, i) => (
              <div
                key={i}
                className={`gp-card${c.featured ? ' gp-card--featured' : ''} gp-reveal`}
                style={{ '--i': i }}
              >
                <div className="gp-card-emoji">{c.emoji}</div>
                <h3 className="gp-card-title">{c.title}</h3>
                <p className="gp-card-text">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── §4 Core Loop ── */}
      <section className="gp-section" id="gp-loop">
        <div className="gp-container">
          <div className="gp-section-label gp-reveal">02 — Core Engagement Loop</div>
          <h2 className="gp-section-h2 gp-reveal">Every node feeds the next. No dead ends.</h2>
          <p className="gp-section-intro gp-reveal">
            The loop has no dead ends. Failing a level still earns partial cheese. Running out of lives
            opens a side game. Finishing an area immediately enters a new one. This is loop continuity —
            the single biggest driver of session length.
          </p>

          <div className="gp-loop-diagram gp-reveal">
            <div className="gp-loop-row">
              {loopTopNodes.map((node, i) => (
                <React.Fragment key={i}>
                  <div className="gp-loop-node">
                    <span className="gp-loop-emoji">{node.emoji}</span>
                    <span className="gp-loop-label">{node.label}</span>
                  </div>
                  {i < loopTopNodes.length - 1 && (
                    <div className="gp-loop-arr">→</div>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="gp-loop-sides">
              <div className="gp-loop-side-arr">↑</div>
              <div className="gp-loop-side-arr">↓</div>
            </div>
            <div className="gp-loop-row gp-loop-row--rev">
              {loopBottomNodes.map((node, i) => (
                <React.Fragment key={i}>
                  <div className="gp-loop-node">
                    <span className="gp-loop-emoji">{node.emoji}</span>
                    <span className="gp-loop-label">{node.label}</span>
                  </div>
                  {i < loopBottomNodes.length - 1 && (
                    <div className="gp-loop-arr">←</div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="gp-insight-box gp-reveal">
            <strong>Key insight:</strong> This is not a 'win levels' loop. It's a
            progression-driven engagement engine. The side systems (Merge, Digging) create re-entry
            points that extend session time by 3–4 minutes and add 2 additional monetization surfaces
            that don't require the player to fail first.
          </div>
        </div>
      </section>

      {/* ── §5 Gameplay Mechanics ── */}
      <section className="gp-section" id="gp-mechanics">
        <div className="gp-container">
          <div className="gp-section-label gp-reveal">03 — Core Mechanics</div>
          <h2 className="gp-section-h2 gp-reveal">
            The one mechanic nobody else has — and the obstacles that make it matter.
          </h2>
          <div className="gp-mechanics-grid">
            <div className="gp-mechanics-left gp-reveal">
              <div className="gp-screenshots-label">BEFORE → AFTER: how difficulty scales</div>

              {/* Slider */}
              <div className="gp-slider">
                <div className="gp-slider-track">
                  {/* slide 0 */}
                  <div className={`gp-slide${mechSlide === 0 ? ' gp-slide--active' : ''}`}>
                    <img
                      src="/gloath/Gloath_level.png"
                      alt="Level 5 clean board"
                      className="gp-slide-img"
                    />
                  </div>
                  {/* slide 1 */}
                  <div className={`gp-slide${mechSlide === 1 ? ' gp-slide--active' : ''}`}>
                    <img
                      src="/gloath/Gloath_level_2.png"
                      alt="Level 32 advanced board with obstacles"
                      className="gp-slide-img"
                    />
                  </div>

                  {/* Prev / Next arrows */}
                  <button
                    className="gp-slider-arrow gp-slider-arrow--prev"
                    onClick={() => setMechSlide(0)}
                    disabled={mechSlide === 0}
                    aria-label="Previous slide"
                  >
                    ‹
                  </button>
                  <button
                    className="gp-slider-arrow gp-slider-arrow--next"
                    onClick={() => setMechSlide(1)}
                    disabled={mechSlide === 1}
                    aria-label="Next slide"
                  >
                    ›
                  </button>
                </div>

                {/* Caption */}
                <div className="gp-screenshot-caption">
                  {mechSlide === 0
                    ? 'Level 5 — Clean board, tap mechanic, zero friction'
                    : 'Level 32 — Cat obstacle + Trap chains + Sewer active'}
                </div>

                {/* Dots */}
                <div className="gp-slider-dots">
                  {[0, 1].map((i) => (
                    <button
                      key={i}
                      className={`gp-dot${mechSlide === i ? ' gp-dot--active' : ''}`}
                      onClick={() => setMechSlide(i)}
                      aria-label={`Slide ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="gp-mechanics-right">
              {mechanicCards.map((c, i) => (
                <div
                  key={i}
                  className={`gp-mechanic-card gp-reveal${c.featured ? ' gp-mechanic-card--featured' : ''}`}
                  style={{ '--i': i }}
                >
                  <div className="gp-mechanic-header">
                    <span className="gp-mechanic-emoji">{c.emoji}</span>
                    <div className="gp-mechanic-titles">
                      <div className="gp-mechanic-title">
                        {c.title}
                        {c.featured && <span className="gp-sig-badge">SIGNATURE</span>}
                      </div>
                      <div className="gp-mechanic-subtitle">{c.subtitle}</div>
                    </div>
                  </div>
                  <p className="gp-mechanic-text">{c.text}</p>
                  <span className="gp-mechanic-tag">{c.tag}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── §6 Level Design ── */}
      <section className="gp-section" id="gp-level-design">
        <div className="gp-container">
          <div className="gp-section-label gp-reveal">04 — Level Design Thinking</div>
          <h2 className="gp-section-h2 gp-reveal">
            Data-driven levels. Every number is a design decision.
          </h2>
          <p className="gp-section-intro gp-reveal">
            Level design is not just 'place some tiles.' Each level has a defined fail rate target, an
            emotional goal, and a monetization role. This infographic shows how three different levels
            serve three different player psychology objectives.
          </p>
          <div className="gp-fullwidth-img-wrap gp-reveal">
            <img
              src="/gloath/Gloath_LevelDesign.png"
              alt="Level design infographic"
              className="gp-fullwidth-img"
            />
          </div>
          <div className="gp-4-grid">
            {levelInsights.map((c, i) => (
              <div key={i} className="gp-card gp-reveal" style={{ '--i': i }}>
                <div className="gp-card-emoji">{c.emoji}</div>
                <h3 className="gp-card-title">{c.title}</h3>
                <p className="gp-card-text">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── §7 Character & Skins ── */}
      <section className="gp-section" id="gp-character">
        <div className="gp-container">
          <div className="gp-section-label gp-reveal">05 — Character &amp; Skin System</div>
          <h2 className="gp-section-h2 gp-reveal">
            Character is not cosmetic. It's the retention system.
          </h2>
          <p className="gp-section-intro gp-reveal">
            Players who feel emotional attachment to a character have 2.3× higher D30 retention than
            players who engage only with the core mechanic. Gloath is engineered to be that anchor.
          </p>
          <div className="gp-fullwidth-img-wrap gp-reveal">
            <img
              src="/gloath/Gloath_Characters.png"
              alt="Gloath character skins — Cheesy Hero, Mozzarella Master, Gold Delight"
              className="gp-fullwidth-img"
            />
          </div>
          <div className="gp-3-grid">
            {skinCards.map((c, i) => (
              <div
                key={i}
                className="gp-card gp-skin-card gp-reveal"
                style={{ '--i': i, '--skin-accent': c.accent }}
              >
                <div className="gp-card-emoji">{c.emoji}</div>
                <h3 className="gp-card-title">{c.title}</h3>
                <div className="gp-skin-badge">{c.badge}</div>
                <p className="gp-card-text">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── §8 KPI Framework ── */}
      <section className="gp-section" id="gp-kpis">
        <div className="gp-container">
          <div className="gp-section-label gp-reveal">06 — KPI Framework</div>
          <h2 className="gp-section-h2 gp-reveal">Every number has a benchmark and a why.</h2>
          <p className="gp-section-intro gp-reveal">
            These are not aspirational numbers. Each target is benchmarked against top-grossing titles
            and tied to a specific product decision.
          </p>
          <div className="gp-kpi-grid">
            {kpiCards.map((k, i) => (
              <div
                key={i}
                className="gp-kpi-card gp-reveal"
                style={{ '--i': i, '--kpi-color': k.color }}
              >
                <div className="gp-kpi-value">{k.value}</div>
                <div className="gp-kpi-metric">{k.metric}</div>
                <div className="gp-kpi-why">
                  <strong>Why this target</strong>
                  <p>{k.why}</p>
                </div>
                <div className="gp-kpi-bench">{k.benchmark}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── §9 RICE ── */}
      <section className="gp-section" id="gp-rice">
        <div className="gp-container">
          <div className="gp-section-label gp-reveal">07 — Feature Prioritization</div>
          <h2 className="gp-section-h2 gp-reveal">RICE scoring. No gut feel, no politics.</h2>
          <p className="gp-section-intro gp-reveal">
            Reach × Impact × Confidence ÷ Effort. Every feature ran through this filter to prevent the
            most common PM failure: building technically exciting features with low retention ROI.
          </p>
          <div className="gp-table-wrap gp-reveal">
            <table className="gp-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Reach</th>
                  <th>Impact</th>
                  <th>Conf.</th>
                  <th>Effort</th>
                  <th>RICE Score</th>
                  <th>Phase</th>
                </tr>
              </thead>
              <tbody>
                {riceRows.map((r, i) => (
                  <tr key={i} className={r.featured ? 'gp-row--featured' : ''}>
                    <td>
                      <span className="gp-table-emoji">{r.emoji}</span> {r.feature}
                    </td>
                    <td>{r.reach}</td>
                    <td>{r.impact}</td>
                    <td>{r.conf}</td>
                    <td>{r.effort}</td>
                    <td className="gp-score-cell">{r.score}</td>
                    <td>
                      <span className={`gp-phase-badge ${phaseClass(r.phase)}`}>{r.phase}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="gp-insight-box gp-reveal">
            <strong>Why Cat scores highest:</strong> 100% reach (every player encounters it) + only 2
            engineering weeks (reuses existing tile collision code) = highest RICE efficiency in the
            entire backlog. This is why it ships in MVP, not Post-Launch.
          </div>
        </div>
      </section>

      {/* ── §10 Monetization ── */}
      <section className="gp-section" id="gp-monetization">
        <div className="gp-container">
          <div className="gp-section-label gp-reveal">08 — Monetization Architecture</div>
          <h2 className="gp-section-h2 gp-reveal">3 emotional entry points. Not just level fail.</h2>
          <p className="gp-section-intro gp-reveal">
            Single-entry-point monetization creates offer fatigue. Gloath intercepts 3 different
            emotional states — frustration, impatience, and desire — each with a proportionate offer.
          </p>
          <div className="gp-mono-grid">
            <div className="gp-mono-left">
              {monetizationMoments.map((m, i) => (
                <div key={i} className="gp-moment-card gp-reveal" style={{ '--i': i }}>
                  <div className="gp-moment-num">{m.num}</div>
                  <div className="gp-moment-body">
                    <div className="gp-moment-title">{m.title}</div>
                    <p className="gp-moment-text">{m.text}</p>
                  </div>
                </div>
              ))}
              <div className="gp-fullwidth-img-wrap gp-reveal" style={{ marginTop: '2rem' }}>
                <img
                  src="/gloath/Gloath_pricing.png"
                  alt="Gloath pricing screen with special offers"
                  className="gp-fullwidth-img"
                />
                <p className="gp-img-caption">
                  Price ladder: $0.99 anchors the entry point (loss aversion window) · $2.99 is the
                  volume driver (best value framing) · $4.99 serves the aspirational buyer (price anchor
                  for Gold Delight skin)
                </p>
              </div>
            </div>

            <div className="gp-mono-right gp-reveal">
              <div className="gp-funnel-title">Conversion Funnel</div>
              <div className="gp-funnel">
                {funnelSteps.map((f, i) => (
                  <div key={i} className="gp-funnel-step">
                    <div className="gp-funnel-bar-wrap">
                      <div
                        className="gp-funnel-bar"
                        style={{ width: f.width, background: f.color }}
                      />
                    </div>
                    <div className="gp-funnel-labels">
                      <span className="gp-funnel-label">{f.label}</span>
                      <span className="gp-funnel-pct" style={{ color: f.color }}>
                        {f.pct}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="gp-funnel-note">
                Whale segment (&lt;1%) drives 40–60% of total IAP revenue in casual F2P.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── §11 Risk Register ── */}
      <section className="gp-section">
        <div className="gp-container">
          <div className="gp-section-label gp-reveal">09 — Risk Register</div>
          <h2 className="gp-section-h2 gp-reveal">A plan without risks is a wish list.</h2>
          <p className="gp-section-intro gp-reveal">
            Every assumption in this document carries a failure mode. These risks are already mitigated
            in the product design — not as afterthoughts.
          </p>
          <div className="gp-risk-list">
            {risks.map((r, i) => (
              <div key={i} className="gp-risk-row gp-reveal" style={{ '--i': i }}>
                <div className="gp-risk-main">
                  <div className="gp-risk-title">{r.title}</div>
                  <div className="gp-risk-pills">
                    <span className={`gp-risk-pill ${r.likelihoodClass}`}>
                      Likelihood: {r.likelihood}
                    </span>
                    <span className={`gp-risk-pill ${r.impactClass}`}>
                      Impact: {r.impact}
                    </span>
                  </div>
                </div>
                <div className="gp-risk-mitigation">
                  <strong>Mitigation:</strong> {r.mitigation}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── §12 Roadmap ── */}
      <section className="gp-section" id="gp-roadmap">
        <div className="gp-container">
          <div className="gp-section-label gp-reveal">10 — Product Roadmap</div>
          <h2 className="gp-section-h2 gp-reveal">4 phases. Clear exit metrics.</h2>
          <p className="gp-section-intro gp-reveal">
            No phase ends until its exit metric is hit. No feature moves to the next phase if the
            current phase isn't validated.
          </p>
          <div className="gp-roadmap-grid">
            {roadmapPhases.map((p, i) => (
              <div
                key={i}
                className="gp-phase-card gp-reveal"
                style={{ '--i': i, '--phase-color': p.dotColor }}
              >
                <div className="gp-phase-dot" style={{ background: p.dotColor }} />
                <div className="gp-phase-name">{p.phase}</div>
                <div className="gp-phase-period">{p.period}</div>
                <ul className="gp-phase-items">
                  {p.items.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
                <div className="gp-phase-exit">
                  <strong>Exit metric:</strong> {p.exit}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── §13 Trade-offs ── */}
      <section className="gp-section">
        <div className="gp-container">
          <div className="gp-section-label gp-reveal">11 — Key Trade-offs</div>
          <h2 className="gp-section-h2 gp-reveal">Every yes has an explicit no.</h2>
          <p className="gp-section-intro gp-reveal">
            Mid-level PMs write down what they chose. Senior PMs also write down what they didn't
            choose — and why.
          </p>
          <div className="gp-tradeoff-table gp-reveal">
            <div className="gp-tradeoff-header">
              <span>Decision</span>
              <span>Chose ✓</span>
              <span>Rejected</span>
              <span>Reasoning</span>
            </div>
            {tradeoffs.map((t, i) => (
              <div key={i} className="gp-tradeoff-row" style={{ '--i': i }}>
                <span className="gp-tradeoff-decision">{t.decision}</span>
                <span className="gp-tradeoff-chose">{t.chose}</span>
                <span className="gp-tradeoff-rejected">{t.rejected}</span>
                <span className="gp-tradeoff-reason">{t.reasoning}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── §14 Closing CTA ── */}
      <section className="gp-section gp-cta-section">
        <div className="gp-container">
          <div className="gp-cta-inner gp-reveal">
            <div className="gp-cta-label">That's Gloath.</div>
            <h2 className="gp-cta-h2">
              A complete product concept.{' '}
              <span className="gp-accent">Every decision has a why.</span>
            </h2>
            <p className="gp-cta-desc">
              From market gap to character design to RICE prioritization to 4-phase roadmap — this case
              study represents end-to-end product thinking across research, design, prioritization, and
              monetization.
            </p>
            <div className="gp-cta-btns">
              <button
                className={`gp-btn gp-btn--primary${isPdfLoading ? ' gp-btn--loading' : ''}`}
                onClick={generatePDF}
                disabled={isPdfLoading}
              >
                {isPdfLoading ? '⏳ Generating PDF…' : '↓ Download Full Case Study PDF'}
              </button>
              <button className="gp-btn gp-btn--ghost" onClick={() => navigate('/')}>
                ← Back to Projects
              </button>
            </div>
          </div>
      </div>
      </section>
    </div>
  );
};

export default GloathPage;
